import React from "react";
import firebase from "firebase/compat/app";
import "firebase/compat/firestore";
import _ from "underscore";
import { groupFormattedPhotosIntoGroups } from "../utils/groupProgressPhotosForDisplay";

/**
 * Progress photos for Media page: one-shot reads (no live listeners) grouped by client.
 *
 * Pass organizationId only while the Progress Photos tab is active to avoid
 * paying for photo/config reads on the Videos tab.
 */
export default function useRecentOrgProgressPhotoGroups(
  organizationId,
  { maxDocs = 250, maxClients = 15 } = {},
) {
  const [clientSections, setClientSections] = React.useState([]);
  const [loading, setLoading] = React.useState(Boolean(organizationId));
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    if (!organizationId) {
      setClientSections([]);
      setLoading(false);
      setError(null);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    const load = async () => {
      try {
        const orgRef = firebase
          .firestore()
          .collection("organizations")
          .doc(organizationId);

        const [orgSnap, photoSnap] = await Promise.all([
          orgRef.get(),
          orgRef
            .collection("progressPhotos")
            .orderBy("createdOn", "desc")
            .limit(maxDocs)
            .get(),
        ]);

        if (cancelled) return;

        const cfg = orgSnap.exists
          ? orgSnap.data()?.progressPhotoConfiguration || []
          : [];
        const progressPhotoConfig = _.sortBy(cfg, "index");
        const getPhotoDisplayName = (typeId) => {
          const photoType = progressPhotoConfig.find((c) => c.id === typeId);
          return photoType ? photoType.name : typeId;
        };

        const byComposite = {};
        photoSnap.docs.forEach((doc) => {
          const data = doc.data();
          const clientId = data.clientId;
          const groupId = data.groupId;
          if (!clientId || !groupId) return;
          const key = `${clientId}::${groupId}`;
          if (!byComposite[key]) {
            byComposite[key] = {
              clientId,
              groupId,
              photos: {},
              metadata: {
                createdOn: data.createdOn?.toDate?.() || new Date(0),
                configuration: data.configuration,
              },
            };
          }
          const g = byComposite[key];
          g.photos[data.type] = data.url;
          const docCreated = data.createdOn?.toDate?.();
          if (docCreated && docCreated > g.metadata.createdOn) {
            g.metadata.createdOn = docCreated;
          }
          if (data.configuration && !g.metadata.configuration) {
            g.metadata.configuration = data.configuration;
          }
        });

        const flatRows = [];
        Object.values(byComposite).forEach((g) => {
          const { clientId, groupId, photos, metadata } = g;
          Object.entries(photos).forEach(([type, url]) => {
            flatRows.push({
              id: `${clientId}-${groupId}-${type}`,
              groupId,
              clientId,
              type,
              displayName: getPhotoDisplayName(type),
              url,
              date: metadata.createdOn,
              configuration: metadata.configuration,
            });
          });
        });

        const byClient = {};
        flatRows.forEach((row) => {
          if (!byClient[row.clientId]) byClient[row.clientId] = [];
          byClient[row.clientId].push(row);
        });

        const sections = Object.entries(byClient).map(([clientId, rows]) => {
          const groupedPhotos = groupFormattedPhotosIntoGroups(rows, null);
          const latestMs = groupedPhotos.length
            ? Math.max(...groupedPhotos.map((g) => new Date(g.date).getTime()))
            : 0;
          return {
            clientId,
            flatPhotos: rows,
            groupedPhotos,
            sortKey: latestMs,
          };
        });

        sections.sort((a, b) => b.sortKey - a.sortKey);
        setClientSections(
          sections.slice(0, maxClients).map(({ sortKey, ...rest }) => rest),
        );
        setError(null);
      } catch (e) {
        console.error("useRecentOrgProgressPhotoGroups:", e);
        if (!cancelled) {
          setError(e.message || "Failed to load progress photos");
          setClientSections([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [organizationId, maxDocs, maxClients]);

  return { clientSections, loading, error };
}
